<script setup lang="ts">
import { Color } from '@previo/codebooks'
import { PBtnType } from '../btn/types'
import { PSpaceSize } from '../space/types'
import PBtn from '../btn/PBtn.vue'
import PSpace from '../space/PSpace.vue'
import PTitleCard from '../title-card/PTitleCard.vue'
import PTextH3 from '../typography/heading/PTextH3.vue'
import PTextTitle2 from '../typography/title/PTextTitle2.vue'
import PTextStandard from '../typography/body/PTextStandard.vue'
import { usePrevioDS } from '../../composables/helpers'

type Props = {
  image: string

  title?: null | string
  description?: null | string

  cardTitle?: null | string
  secondaryDescription?: null | string

  primaryBtn?: null | string
  primaryBtnIcon?: null | string
  secondaryBtn?: null | string
  secondaryBtnIcon?: null | string

  footerTitle?: null | string
}

type Emits = {
  (e: 'primary', evt: Event): void
  (e: 'secondary', evt: Event): void
}

defineProps<Props>()
const emits = defineEmits<Emits>()

const ds = usePrevioDS()
</script>

<template>
  <div class="ds-visual-up-sell">
    <div
      class="p-flex"
      :class="
        ds.screen().isLaptopAndLarge
          ? 'p-flex-direction-row p-items-start'
          : 'p-flex-direction-column'
      "
    >
      <div class="p-flex-shrink-0 p-flex p-justify-center">
        <img :src="image" :width="ds.screen().isLaptopAndLarge ? 400 : 260" alt="" />
      </div>

      <p-space :mobile="PSpaceSize.XXL" :vertical="ds.screen().isMobile" />

      <div class="p-flex-1 p-min-width-0">
        <template v-if="title || description">
          <p-text-h3 v-if="title" :text="title" />
          <p-space v-if="title && description" :mobile="PSpaceSize.SM" vertical />
          <p-text-standard v-if="description" :text="description" :color="Color.GREY_500" />
        </template>

        <template v-if="$slots.card">
          <p-space :mobile="PSpaceSize.LG" vertical />
          <p-title-card :title="cardTitle" :color="Color.GREY_50">
            <slot name="card" />
          </p-title-card>
        </template>

        <template v-if="secondaryDescription">
          <p-space :mobile="PSpaceSize.LG" vertical />
          <p-text-standard :text="secondaryDescription" :color="Color.GREY_500" />
        </template>

        <template v-if="primaryBtn || secondaryBtn">
          <p-space :mobile="PSpaceSize.LG" vertical />

          <div
            :class="
              ds.screen().isLaptopAndLarge
                ? 'p-flex p-items-center p-no-wrap'
                : 'p-flex p-flex-direction-column'
            "
          >
            <p-btn
              v-if="primaryBtn"
              :text="primaryBtn"
              :icon-end="primaryBtnIcon"
              :color="Color.POSITIVE"
              :grow="ds.screen().isMobile"
              class="p-laptop-and-large-order-3"
              cy="primary-btn"
              @click="(e: Event) => emits('primary', e)"
            />
            <p-space
              :mobile="PSpaceSize.XS"
              :vertical="ds.screen().isMobile"
              class="p-laptop-and-large-order-2"
            />

            <p-btn
              v-if="secondaryBtn"
              :type="PBtnType.OUTLINE"
              :text="secondaryBtn"
              :icon-end="secondaryBtnIcon"
              :grow="ds.screen().isMobile"
              class="p-laptop-and-large-order-1"
              cy="secondary-btn"
              @click="(e: Event) => emits('secondary', e)"
            />
          </div>
        </template>
      </div>
    </div>

    <template v-if="footerTitle || $slots.footer">
      <p-space :mobile="PSpaceSize.LG" vertical />
      <div class="ds-visual-up-sell-footer">
        <p-text-title2 v-if="footerTitle" :text="footerTitle" :color="Color.GREY_600" />
        <p-space v-if="footerTitle && $slots.footer" :mobile="PSpaceSize.XS" vertical />
        <slot name="footer" />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.ds-visual-up-sell {
  max-width: 856px;
  margin: 0 auto;
}

.ds-visual-up-sell-footer {
  max-width: 600px;
}
</style>
